import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { PricingBaseComponent } from '@luggagehero/features/ui';
import { ProductInfo } from '@luggagehero/shared/interfaces';
import { SharedPricingService } from '@luggagehero/shared/services/pricing';
import {
  shouldShowStoragePriceTable,
  shouldShowWalkInNote,
  StoragePriceTier,
  storagePriceTiersFromProducts,
} from '@luggagehero/shared/ui';

@Component({
  selector: 'lh-pricing',
  templateUrl: './pricing.component.html',
  styleUrls: ['./pricing.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PricingComponent extends PricingBaseComponent {
  private priceFormatter = inject(SharedPricingService);

  private priceTiersFor: ProductInfo[] | undefined;
  private priceTiersCache: StoragePriceTier[] = [];

  /**
   * The price table's columns (CTR-51): booked ahead, booked today, and walked in when the shop has
   * walk-in prices. Empty when the shop's products are not loaded (e.g. opened from the navbar), and
   * the modal then shows the plain hourly and daily price instead.
   */
  public get priceTiers(): StoragePriceTier[] {
    const products = this.shop?.products;
    if (products !== this.priceTiersFor) {
      this.priceTiersFor = products;
      this.priceTiersCache = storagePriceTiersFromProducts(products);
    }
    return this.priceTiersCache;
  }

  /** The table, or else the plain hourly and daily price (see `shouldShowStoragePriceTable`). */
  public get showPriceTable(): boolean {
    return shouldShowStoragePriceTable(this.priceTiers);
  }

  /** The "walk-in rates may be higher" note (see `shouldShowWalkInNote`). */
  public get showWalkInNote(): boolean {
    return shouldShowWalkInNote(this.priceTiers);
  }

  /** Always two decimals, so the columns line up (kr 10.00 next to kr 12.50, not kr 10). */
  public readonly formatPrice = (amount: number): string =>
    this.priceFormatter.format(amount, this.pricing?.currency ?? '', 2, false);
}
